import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import Offcanvas from 'bootstrap/js/dist/offcanvas';

gsap.registerPlugin(ScrollTrigger);

// Mobile browsers resize the viewport when the address bar collapses/
// expands, firing a `resize` that by default makes ScrollTrigger re-measure
// every trigger and jump scroll-linked/pinned elements to match — visible
// as the page snapping up then back down. Any interaction hiding the
// address bar (e.g. tapping the mobile hamburger) triggered this.
// `ignoreMobileResize` is GSAP's built-in opt-out for this resize class
// (still refreshes on real resizes like orientation change).
ScrollTrigger.config({ ignoreMobileResize: true });

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

if (!prefersReducedMotion) {
    // autoResize:false — Lenis's own built-in resize handling (unlike GSAP's
    // ignoreMobileResize above) has no opt-out for the mobile address-bar
    // class of `resize` events: it unconditionally snaps scroll position to
    // match the new viewport height, which is exactly the up-then-down 1-2px
    // jump on hamburger tap. Replaced below with the same width/height
    // dimensions Lenis tracks internally, just filtered so a height-only
    // resize (address bar) doesn't trigger it — width changes (real resize,
    // orientation change) and content height changes (images loading,
    // accordions opening) still do.
    const lenis = new Lenis({
        smoothWheel: true,
        duration: 1.1,
        wheelMultiplier: 0.9,
        touchMultiplier: 1,
        autoResize: false,
    });
    window.__omeconLenis = lenis;

    let lastInnerWidth = window.innerWidth;
    const onWindowResize = () => {
        if (window.innerWidth !== lastInnerWidth) {
            lastInnerWidth = window.innerWidth;
            lenis.resize();
        }
    };
    window.addEventListener('resize', onWindowResize, { passive: true });

    const contentResizeObserver = new ResizeObserver(() => lenis.resize());
    contentResizeObserver.observe(document.documentElement);

    lenis.on('scroll', ScrollTrigger.update);

    gsap.ticker.add((time) => {
        lenis.raf(time * 1000);
    });

    gsap.ticker.lagSmoothing(0);
}

document.addEventListener('DOMContentLoaded', function () {
    const adminBar = document.querySelector('#wpadminbar');
    const wrapper = document.querySelector('#wrapper-navbar');
    const nav = wrapper ? wrapper.querySelector('nav') : null;

    if (adminBar && nav) {
        nav.style.marginTop = adminBar.offsetHeight + 'px';
    }

    // Scroll detection: add .is-scrolled when user scrolls past hero.
    // Desktop only (≥1140px, this theme's "lg") — also hides the navbar on
    // scroll DOWN and slides it back in on scroll UP (see .is-nav-hidden in
    // _header.scss). Mobile never gets .is-nav-hidden, navbar stays put.
    if (wrapper) {
        const navDesktopMinWidth = 1140;
        let lastScrollY = window.scrollY;

        const onScroll = () => {
            const scrollY = window.scrollY;
            wrapper.classList.toggle('is-scrolled', scrollY > 80);

            if (window.innerWidth >= navDesktopMinWidth) {
                const scrollingDown = scrollY > lastScrollY;
                wrapper.classList.toggle('is-nav-hidden', scrollingDown && scrollY > 80);
            } else {
                wrapper.classList.remove('is-nav-hidden');
            }

            lastScrollY = scrollY;
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
    }

    // Mobile menu (#omeconMobileMenu) is a small anchored dropdown panel,
    // not a full-screen takeover, so its trigger has data-bs-backdrop=
    // "false" (Bootstrap's dimmed backdrop sits at z-index 1040/1045, above
    // this panel, making it invisible/unclickable and its close button
    // unreachable — looked "stuck"). Without a backdrop, Bootstrap no
    // longer auto-closes on outside click, so it's handled here instead.
    const mobileMenu = document.getElementById('omeconMobileMenu');
    if (mobileMenu) {
        document.addEventListener('click', (event) => {
            if (!mobileMenu.classList.contains('show')) {
                return;
            }
            const isToggle = event.target.closest('[data-bs-target="#omeconMobileMenu"]');
            if (mobileMenu.contains(event.target) || isToggle) {
                return;
            }
            const instance = Offcanvas.getInstance(mobileMenu);
            if (instance) {
                instance.hide();
            }
        });
    }

    // Nav submenus (desktop dropdown/flyout + mobile accordion) — the
    // toggle button is a sibling of the item's <a>, not nested inside it
    // (see Omecon_Nav_Walker), so it needs its own click handling: prevent
    // the parent <a> navigating, toggle this level open/closed, and close
    // any other item open at the same level (accordion-style, matters most
    // on mobile where flyout-on-hover isn't available).
    const toggleNavItem = (item, toggle) => {
        const parentUl = item.parentElement;
        const isOpen = item.classList.contains('is-open');

        parentUl.querySelectorAll(':scope > li.is-open').forEach((sibling) => {
            if (sibling === item) return;
            sibling.classList.remove('is-open');
            sibling.querySelector(':scope > .omecon-nav__toggle')?.setAttribute('aria-expanded', 'false');
        });

        item.classList.toggle('is-open', !isOpen);
        toggle?.setAttribute('aria-expanded', String(!isOpen));
    };

    document.querySelectorAll('.omecon-nav__menu, .omecon-mobile-menu').forEach((menu) => {
        menu.addEventListener('click', (event) => {
            const toggle = event.target.closest('.omecon-nav__toggle');
            if (toggle && menu.contains(toggle)) {
                event.preventDefault();
                event.stopPropagation();
                toggleNavItem(toggle.closest('li'), toggle);
                return;
            }

            // Parent items whose "Custom Link" href is just a placeholder
            // anchor (e.g. "#produkty" with no matching in-page element,
            // set that way in the WP menu editor because the item has no
            // real landing page) should open the submenu on click instead
            // of falling through to native anchor navigation.
            const link = event.target.closest('a');
            if (!link || !menu.contains(link)) return;
            const item = link.closest('li');
            if (!item || !item.classList.contains('menu-item-has-children')) return;
            if (link.parentElement !== item) return;

            const href = link.getAttribute('href') || '';
            if (!href.startsWith('#')) return;
            if (href.length > 1 && document.getElementById(href.slice(1))) return;

            event.preventDefault();
            event.stopPropagation();
            toggleNavItem(item, item.querySelector(':scope > .omecon-nav__toggle'));
        });
    });

    // Close any open desktop submenu when clicking anywhere outside of it
    // (submenus only open on click now, not hover, so they need an explicit
    // close-on-outside-click instead of relying on :hover leaving).
    document.addEventListener('click', (event) => {
        document.querySelectorAll('.omecon-nav__menu li.is-open').forEach((item) => {
            if (item.contains(event.target)) return;
            item.classList.remove('is-open');
            item.querySelector(':scope > .omecon-nav__toggle')?.setAttribute('aria-expanded', 'false');
        });
    });

    // Close open submenus whenever the mobile offcanvas panel itself closes,
    // so re-opening it doesn't show stale expanded state.
    if (mobileMenu) {
        mobileMenu.addEventListener('hidden.bs.offcanvas', () => {
            mobileMenu.querySelectorAll('.omecon-mobile-menu li.is-open').forEach((li) => {
                li.classList.remove('is-open');
                li.querySelector(':scope > .omecon-nav__toggle')?.setAttribute('aria-expanded', 'false');
            });
        });

        // Bootstrap's own data-bs-scroll="false" body-overflow lock only
        // stops native touch/scrollbar scrolling — it doesn't touch Lenis,
        // which hijacks wheel/touch itself for smooth-scroll and keeps
        // scrolling the page underneath regardless. Pausing Lenis while the
        // panel is open makes it preventDefault() every wheel/touch event
        // instead, blocking page scroll no matter the input device.
        mobileMenu.addEventListener('show.bs.offcanvas', () => {
            window.__omeconLenis?.stop();
        });
        mobileMenu.addEventListener('hidden.bs.offcanvas', () => {
            window.__omeconLenis?.start();
        });
    }

    // Language switcher (desktop .omecon-nav__lang-wrap + mobile
    // .omecon-mobile-panel__lang-wrap) — same click-to-toggle /
    // close-on-outside-click pattern as the nav submenus above.
    const langWraps = document.querySelectorAll('.omecon-nav__lang-wrap, .omecon-mobile-panel__lang-wrap');
    if (langWraps.length) {
        langWraps.forEach((wrap) => {
            const toggle = wrap.querySelector('.omecon-nav__lang, .omecon-mobile-panel__lang');
            if (!toggle) return;

            toggle.addEventListener('click', (event) => {
                event.stopPropagation();
                const isOpen = wrap.classList.contains('is-open');

                langWraps.forEach((otherWrap) => {
                    if (otherWrap === wrap) return;
                    otherWrap.classList.remove('is-open');
                    otherWrap.querySelector('.omecon-nav__lang, .omecon-mobile-panel__lang')?.setAttribute('aria-expanded', 'false');
                });

                wrap.classList.toggle('is-open', !isOpen);
                toggle.setAttribute('aria-expanded', String(!isOpen));
            });
        });

        document.addEventListener('click', (event) => {
            langWraps.forEach((wrap) => {
                if (wrap.contains(event.target)) return;
                wrap.classList.remove('is-open');
                wrap.querySelector('.omecon-nav__lang, .omecon-mobile-panel__lang')?.setAttribute('aria-expanded', 'false');
            });
        });
    }

    // Section 4 — module tabs
    const s4Tabs = document.querySelectorAll('.omecon-s4__tab');
    if (s4Tabs.length) {
        const activateS4Tab = (tab) => {
            const panelId = tab.getAttribute('aria-controls');
            const panel = document.getElementById(panelId);
            if (!panel || tab.classList.contains('is-active')) return;

            // deactivate all
            s4Tabs.forEach((t) => {
                t.classList.remove('is-active');
                t.setAttribute('aria-selected', 'false');
            });
            document.querySelectorAll('.omecon-s4__panel').forEach((p) => {
                p.classList.remove('is-active');
                p.setAttribute('hidden', '');
            });

            // activate this one
            tab.classList.add('is-active');
            tab.setAttribute('aria-selected', 'true');
            panel.classList.add('is-active');
            panel.removeAttribute('hidden');
        };

        // Hover-driven, not click — mobile uses a completely separate
        // accordion (.omecon-s4__mobile-list) so this tablist is only ever
        // shown on desktop/tablet, where hover exists. `focus` covers
        // keyboard navigation (Tab key) the same way hover covers mouse.
        s4Tabs.forEach((tab) => {
            tab.addEventListener('mouseenter', () => activateS4Tab(tab));
            tab.addEventListener('focus', () => activateS4Tab(tab));
        });
    }

    // Shared single-open accordion: inline max-height (from scrollHeight) +
    // CSS transition, instead of toggling [hidden] (display:none can't be
    // CSS-transitioned, so it used to snap open/closed with no animation).
    // One `wrap` per instance, items inside each toggle exclusively (opening
    // one closes the rest). Used by section 4 (mobile module list) and
    // section 10 (FAQ).
    const initAccordion = (wrapSelector, itemSelector, toggleSelector, bodySelector) => {
        document.querySelectorAll(wrapSelector).forEach((wrap) => {
            const items = Array.from(wrap.querySelectorAll(itemSelector));

            const setOpen = (item, isOpen) => {
                item.classList.toggle('is-open', isOpen);

                const toggle = item.querySelector(toggleSelector);
                if (toggle) {
                    toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
                }

                const body = item.querySelector(bodySelector);
                if (body) {
                    body.style.maxHeight = isOpen ? body.scrollHeight + 'px' : '0px';
                }
            };

            items.forEach((item) => {
                // Reflect the server-rendered initial state (first item open)
                // with an explicit px height, since CSS alone only sets 0.
                setOpen(item, item.classList.contains('is-open'));

                const toggle = item.querySelector(toggleSelector);
                if (!toggle) return;

                toggle.addEventListener('click', () => {
                    const wasOpen = item.classList.contains('is-open');
                    items.forEach((other) => setOpen(other, false));
                    if (!wasOpen) {
                        setOpen(item, true);
                    }
                });
            });

            // Re-measure the open item if content reflows (resize, orientation
            // change) — the inline px height would otherwise go stale.
            window.addEventListener('resize', () => {
                const openItem = items.find((item) => item.classList.contains('is-open'));
                if (openItem) {
                    const body = openItem.querySelector(bodySelector);
                    if (body) {
                        body.style.maxHeight = body.scrollHeight + 'px';
                    }
                }
            });
        });
    };

    initAccordion('.omecon-s4__mobile-list', '.omecon-s4__mobile-item', '.omecon-s4__mobile-toggle', '.omecon-s4__mobile-desc');

    // Shared by section 15 (result counters) and section 12/7 (deployment
    // scroll stack) below.
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Section 16 — result counters, counting up from 1 to the ACF-supplied
    // target once the number scrolls into view. The target value is already
    // server-rendered in the markup (SEO / no-JS safe); JS resets to "1" and
    // animates back up only when motion isn't reduced.
    document.querySelectorAll('.omecon-s16__number').forEach((el) => {
        const target = parseInt(el.getAttribute('data-count-to'), 10);

        if (!target || reduceMotion) {
            return;
        }

        // The leading "+"/"-" and anything after the integer (decimal comma,
        // %, units, ...) are literal text, not part of the counted number —
        // only the unsigned integer itself is animated (see PHP template).
        const prefix = el.getAttribute('data-prefix') || '';
        const suffix = el.getAttribute('data-suffix') || '';
        const counter = { value: 1 };
        el.textContent = prefix + '1' + suffix;

        ScrollTrigger.create({
            trigger: el,
            start: 'top 85%',
            once: true,
            onEnter: () => {
                gsap.to(counter, {
                    value: target,
                    duration: 1.6,
                    ease: 'power1.out',
                    onUpdate: () => {
                        el.textContent = prefix + Math.round(counter.value) + suffix;
                    },
                });
            },
        });
    });

    // Section 17 (process timeline) + section 22 mobile (both "numbered" and
    // "icons" variants) — the vertical line "fills" (navy) in sync with
    // scroll progress through the steps list, and each dot lights up (navy)
    // once the fill line reaches it. Same mechanism, different class names
    // per section (s22's line is mobile-only — hidden via CSS on desktop,
    // so this still runs harmlessly there, just with nothing visible). A
    // page can have more than one such section, so each gets its own
    // independent ScrollTrigger.
    document.querySelectorAll('.omecon-s17__steps-wrap, .omecon-s22__steps-wrap').forEach((wrap) => {
        const fill = wrap.querySelector('.omecon-s17__line-fill, .omecon-s22__step-line-fill');
        const dots = wrap.querySelectorAll('.omecon-s17__dot, .omecon-s22__step-dot');

        if (!fill) {
            return;
        }

        if (reduceMotion) {
            fill.style.height = '100%';
            dots.forEach((dot) => dot.classList.add('is-active'));
            return;
        }

        gsap.set(fill, { height: '0%' });

        gsap.to(fill, {
            height: '100%',
            ease: 'none',
            scrollTrigger: {
                trigger: wrap,
                start: 'top 70%',
                end: 'bottom 70%',
                scrub: true,
                onUpdate: (self) => {
                    const wrapRect = wrap.getBoundingClientRect();

                    dots.forEach((dot) => {
                        const dotRect = dot.getBoundingClientRect();
                        const dotProgress = (dotRect.top + dotRect.height / 2 - wrapRect.top) / wrapRect.height;

                        dot.classList.toggle('is-active', self.progress >= dotProgress);
                    });
                },
            },
        });
    });

    // Section 22 (process) — desktop step positions ("inverted S": row 1
    // L→R, row 2 R→L, row 3 L→R) computed here instead of hardcoded in CSS
    // (:nth-of-type), since step count varies per page (11–13). Rows 1 and
    // 2 always take 4 steps each, row 3 gets the remainder (3–5). Row 2
    // reuses row 1's SAME 4 x-positions (mirrored) instead of computing its
    // own from the narrower SVG line segment (x=1233.6→145.598, no curve) —
    // otherwise columns 1↔8, 2↔7 etc. wouldn't line up vertically as in the
    // design. Row 3 gets its own grid (different step count), spread across
    // the full SVG line width (x=145.598→1366, see section22-omecon.php),
    // y ≈ 1/556, 265.805/556, 555/556.
    document.querySelectorAll('.omecon-s22__steps').forEach((stepsEl) => {
        const steps = Array.from(stepsEl.children).filter((el) => el.matches('.omecon-s22__step'));

        if (!steps.length) {
            return;
        }

        // 1140–1319px (lg, before xl) — step titles get equal height (matched
        // to the tallest one) so a card with a short one-line title doesn't
        // sit at a different vertical spot than one with a two-line title.
        // `min-height` via JS because titles are position:absolute at "lg"
        // and up (see the row-position matchMedia below) — no flex/grid
        // stretch across siblings to lean on. Kept OUTSIDE the 9-step guard
        // below — that guard is only for the "inverted S letter" row
        // layout, which needs a specific step count; this equal-height fix
        // applies regardless of how many steps a page actually has.
        const titles = steps
            .map((step) => step.querySelector('.omecon-s22__step-title'))
            .filter(Boolean);

        if (titles.length > 1) {
            const titlesMedia = gsap.matchMedia();

            titlesMedia.add('(min-width: 1140px) and (max-width: 1319.98px)', () => {
                const remeasure = () => {
                    gsap.set(titles, { clearProps: 'minHeight' });
                    const tallest = Math.max(...titles.map((title) => title.getBoundingClientRect().height));
                    gsap.set(titles, { minHeight: tallest });
                };

                remeasure();

                // Re-measure once the webfont is actually loaded — the first
                // pass can run against the fallback font's metrics (narrower
                // or wider glyphs re-wrap the text differently), leaving a
                // stale/too-short minHeight once "Plus Jakarta Sans" swaps in.
                if (document.fonts && document.fonts.ready) {
                    document.fonts.ready.then(remeasure);
                }

                let resizeRaf = null;
                const onResize = () => {
                    if (resizeRaf) {
                        cancelAnimationFrame(resizeRaf);
                    }
                    resizeRaf = requestAnimationFrame(remeasure);
                };
                window.addEventListener('resize', onResize, { passive: true });

                return () => {
                    window.removeEventListener('resize', onResize);
                    gsap.set(titles, { clearProps: 'minHeight' });
                };
            });
        }

        const centerOfSlice = (startFrac, endFrac, count, index) => (
            startFrac + ((endFrac - startFrac) / count) * (index + 0.5)
        );

        const media = gsap.matchMedia();

        media.add('(min-width: 1140px)', () => {
            const n = steps.length;

            // Same 3 shape thresholds as omecon_s22_connector_svg() in
            // section22-omecon.php — keep both in sync if this ever changes.
            //  ≤4   → 1 row, straight line across full width (xRange 0–1,
            //         not 1367-based — no curve to reserve space for).
            //  5–8  → 2 rows, one curve (row 0: x=1→1233.6, row 1:
            //         x=145.598→1233.6 reversed), topPct 0/100.
            //  9+   → 3 rows, full "inverted S" (original design).
            let rowCounts;
            let rowTopPct;
            let rowXRange;
            let rowReversed;

            if (n <= 4) {
                rowCounts = [n];
                rowTopPct = [0];
                rowXRange = [[0, 1]];
                rowReversed = [false];
            } else if (n <= 8) {
                rowCounts = [4, n - 4];
                rowTopPct = [0, 100];
                rowXRange = [[1 / 1367, 1233.6 / 1367], [145.598 / 1367, 1233.6 / 1367]];
                rowReversed = [false, true];
            } else {
                rowCounts = [4, 4, n - 8];
                rowTopPct = [0, (265.805 / 556) * 100, (555 / 556) * 100];
                rowXRange = [
                    [1 / 1367, 1233.6 / 1367],
                    [145.598 / 1367, 1233.6 / 1367],
                    [145.598 / 1367, 1366 / 1367],
                ];
                rowReversed = [false, true, false];
            }

            // Row 1 (index 0) always computes its 4 x-positions once — any
            // other row with exactly 4 items reuses them (mirrored) instead
            // of computing its own from a narrower line segment, so columns
            // line up vertically (see comment in section22-omecon.php).
            const row0Left = rowCounts[0] === 4
                ? [0, 1, 2, 3].map((i) => centerOfSlice(rowXRange[0][0], rowXRange[0][1], 4, i) * 100)
                : null;

            let cursor = 0;

            rowCounts.forEach((count, rowIndex) => {
                for (let j = 0; j < count; j += 1) {
                    const step = steps[cursor];
                    let leftPct;

                    if (rowIndex === 1 && count === 4 && row0Left) {
                        leftPct = row0Left[3 - j];
                    } else {
                        const index = rowReversed[rowIndex] ? count - 1 - j : j;
                        leftPct = centerOfSlice(rowXRange[rowIndex][0], rowXRange[rowIndex][1], count, index) * 100;
                    }

                    gsap.set(step, {
                        position: 'absolute',
                        left: leftPct + '%',
                        top: rowTopPct[rowIndex] + '%',
                        xPercent: -50,
                        y: -36, // half the dot's height (72px / 2)
                    });

                    cursor += 1;
                }
            });

            // Desktop line-draw + step reveal (2026-09-17) — previously this
            // section had NO animation at all on desktop (steps just appeared
            // in their final position instantly, static SVG line). Mirrors
            // the mobile fill-line/dot-activate idea above: as the section
            // scrolls through, the `.omecon-s22__steps-line-progress` overlay
            // path (see omecon_s22_connector_svg() in section22-omecon.php)
            // draws itself via stroke-dashoffset, and each step fades/rises
            // in, staggered in the same order the line visually connects
            // them (DOM order already matches step numbering 1..n along the
            // "inverted S").
            const progressPath = stepsEl.querySelector('.omecon-s22__steps-line-progress');
            let scrollTrigger;

            if (reduceMotion) {
                if (progressPath) {
                    gsap.set(progressPath, { opacity: 1 });
                }
                gsap.set(steps, { opacity: 1, y: 0 });
            } else {
                // Steps already sit at y:-36 (centers the ball on the line,
                // set above) — start 16px lower than that (y:-20) and
                // animate back to -36, so they rise slightly into place
                // instead of losing their centering offset.
                gsap.set(steps, { opacity: 0, y: -20 });

                const timeline = gsap.timeline({
                    scrollTrigger: {
                        trigger: stepsEl,
                        start: 'top 70%',
                        end: 'bottom 70%',
                        scrub: true,
                    },
                });

                if (progressPath) {
                    const pathLength = progressPath.getTotalLength();
                    gsap.set(progressPath, { opacity: 1, strokeDasharray: pathLength, strokeDashoffset: pathLength });
                    timeline.to(progressPath, { strokeDashoffset: 0, ease: 'none' }, 0);
                }

                timeline.to(steps, {
                    opacity: 1,
                    y: -36, // keep the ball's own centering offset (see gsap.set above)
                    ease: 'none',
                    stagger: { each: 1 / steps.length, from: 'start' },
                }, 0);

                scrollTrigger = timeline.scrollTrigger;
            }

            return () => {
                if (scrollTrigger) {
                    scrollTrigger.kill();
                }
                if (progressPath) {
                    gsap.set(progressPath, { clearProps: 'opacity,strokeDasharray,strokeDashoffset' });
                }
                gsap.set(steps, { clearProps: 'position,left,top,transform,opacity' });
            };
        });
    });

    // Deployment cards scroll stack — shared by section 7 ("Our deployments",
    // CPT-driven) and section 12 ("Deployments", freeform layout). Both render
    // the same .omecon-deployments markup, and a page can have more than one
    // instance, so each match gets its own independent matchMedia + timeline.
    document.querySelectorAll('.omecon-deployments').forEach((deployments) => {
        if (reduceMotion) {
            return;
        }

        const media = gsap.matchMedia();

        media.add('(min-width: 1140px)', () => {
            const list = deployments.querySelector('.omecon-deployments__list');
            const cards = gsap.utils.toArray('.omecon-deployments__card', deployments);

            if (!list || cards.length < 2) {
                return undefined;
            }

            const stripOffset = () => 38;
            const stickyTop = 120;
            const coveredScale = (level) => Math.max(0.88, 1 - ((level * 32) / cards[0].offsetWidth));

            list.classList.add('is-gsap-stacked');

            gsap.set(cards, {
                clearProps: 'transform',
                '--omecon-stack-top': (index) => `${stickyTop + (index * stripOffset())}px`,
                scale: 1,
                zIndex: (index) => index + 1,
            });

            const timeline = gsap.timeline({
                scrollTrigger: {
                    trigger: list,
                    start: `top top+=${stickyTop}`,
                    end: () => `+=${Math.max(cards.length * 220, list.offsetHeight - cards[0].offsetHeight)}`,
                    scrub: 0.55,
                    invalidateOnRefresh: true,
                },
            });

            cards.slice(1).forEach((card, index) => {
                const cardIndex = index + 1;

                cards.slice(0, cardIndex).forEach((previousCard, previousIndex) => {
                    timeline.to(previousCard, {
                        scale: () => coveredScale(cardIndex - previousIndex),
                        ease: 'none',
                        duration: 1,
                    }, index);
                });
            });

            ScrollTrigger.refresh();

            return () => {
                timeline.scrollTrigger.kill();
                timeline.kill();
                list.classList.remove('is-gsap-stacked');
                cards.forEach((card) => card.style.removeProperty('--omecon-stack-top'));
                gsap.set(cards, { clearProps: 'transform,zIndex' });
            };
        });
    });

    // Section 10 (FAQ) — plain accordion (see initAccordion above),
    // deliberately not Bootstrap's data-bs-toggle="collapse" (its
    // JS-computed height animation visibly jumped the page on close, and
    // its aria-expanded updates were a second source of truth alongside
    // .is-open). A pure-CSS `grid-template-rows: 0fr` attempt didn't
    // actually collapse in practice, so height is set explicitly (inline
    // `max-height` in px from `scrollHeight`) and transitioned via CSS.
    initAccordion('.omecon-s10__accordion', '.omecon-s10__item', '.omecon-s10__toggle', '.omecon-s10__body');

    // Section 15 ("grid" variant only — icon + title + description tiles,
    // NOT "duo"/"grid_featured") — cards equalized to the tallest one, JS
    // instead of a guessed fixed CSS min-height: Bootstrap's `.row` flexbox
    // `align-items:stretch` (+ `h-100`) already matches heights WITHIN a
    // wrapped row, but different rows can still end up different heights
    // depending on description text wrap — same measure/remeasure pattern
    // as the s22 step-title fix above. Desktop only (lg/1140px up) — below
    // that, cards stack full-width and size to their own content.
    document.querySelectorAll('.omecon-s15--grid .omecon-s15__grid').forEach((grid) => {
        const cards = Array.from(grid.querySelectorAll('.omecon-s15__card'));

        if (cards.length < 2) {
            return;
        }

        const media = gsap.matchMedia();

        media.add('(min-width: 1140px)', () => {
            const remeasure = () => {
                // Zamiast 'clearProps', ustawiamy 0, żeby nadpisać CSS-owe 'min-height: 500px'
                // Dzięki temu karty skurczą się do faktycznej wysokości contentu przed pomiarem.
                gsap.set(cards, { minHeight: 0 });
                const tallest = Math.max(...cards.map((card) => card.getBoundingClientRect().height));
                gsap.set(cards, { minHeight: tallest + 100 });
            };

            remeasure();

            // Re-measure once the webfont is actually loaded — the first
            // pass can run against the fallback font's metrics, leaving a
            // stale/too-short minHeight once "Plus Jakarta Sans" swaps in.
            if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(remeasure);
            }

            let resizeRaf = null;
            const onResize = () => {
                if (resizeRaf) {
                    cancelAnimationFrame(resizeRaf);
                }
                resizeRaf = requestAnimationFrame(remeasure);
            };
            window.addEventListener('resize', onResize, { passive: true });

            return () => {
                window.removeEventListener('resize', onResize);
                gsap.set(cards, { clearProps: 'minHeight' });
            };
        });
    });

    // Section 15 ("grid_featured" variant) — same equal-height problem as
    // the "grid" variant above, but flex-wrap here means the non-active
    // narrow (1/3) cards can land on a different wrapped line than the
    // active/widened one, so CSS align-items:stretch (per-line only) never
    // matches them to it — a narrow card looked shorter whenever the wide
    // one needed more than the 500px/400px CSS floor. Also re-measures on
    // hover, since swapping which tile is active changes its own height.
    document.querySelectorAll('.omecon-s15--grid_featured .omecon-s15__cards').forEach((cardsWrap) => {
        const cards = Array.from(cardsWrap.querySelectorAll('.omecon-s15__card'));

        if (cards.length < 2) {
            return;
        }

        const media = gsap.matchMedia();

        media.add('(min-width: 1140px)', () => {
            const remeasure = () => {
                // Zamiast 'clearProps', ustawiamy 0, żeby nadpisać CSS-owe 'min-height: 500px'
                // Dzięki temu karty skurczą się do faktycznej wysokości contentu przed pomiarem.
                gsap.set(cards, { minHeight: 0 });
                const tallest = Math.max(...cards.map((card) => card.getBoundingClientRect().height));
                gsap.set(cards, { minHeight: tallest + 75 });
            };

            remeasure();

            if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(remeasure);
            }

            let resizeRaf = null;
            const onResize = () => {
                if (resizeRaf) {
                    cancelAnimationFrame(resizeRaf);
                }
                resizeRaf = requestAnimationFrame(remeasure);
            };
            window.addEventListener('resize', onResize, { passive: true });

            // Debounced (a mouseenter fires per card, not once) — one rAF was
            // too early: the card's `flex-basis` transition (0.35s, see
            // `.omecon-s15__card` in _s15-benefits-tiles.scss) has barely
            // started by the next frame, so the just-activated card is still
            // near its OLD (narrow) width and its text measures as taller
            // than its final (wide) height — that inflated minHeight then
            // got baked into ALL cards for the whole hover, making the row
            // (and everything below it) jump on the very first hover instead
            // of settling smoothly. Wait out the actual transition instead.
            const HOVER_TRANSITION_MS = 350; // keep in sync with the SCSS
            let hoverTimer = null;
            const onHoverChange = () => {
                if (hoverTimer) {
                    clearTimeout(hoverTimer);
                }
                hoverTimer = setTimeout(remeasure, HOVER_TRANSITION_MS);
            };
            cardsWrap.addEventListener('mouseenter', onHoverChange, true);
            cardsWrap.addEventListener('mouseleave', onHoverChange, true);

            return () => {
                window.removeEventListener('resize', onResize);
                cardsWrap.removeEventListener('mouseenter', onHoverChange, true);
                cardsWrap.removeEventListener('mouseleave', onHoverChange, true);
                gsap.set(cards, { clearProps: 'minHeight' });
            };
        });
    });
});

jQuery(function ($) {
    $('body').on('added_to_cart', function (event, fragments, cart_hash, $button) {
        var cartToggle = document.querySelector('[data-bs-target="#miniCartOffcanvas"]');

        if (!cartToggle) {
            return;
        }

        cartToggle.click();
    });
});


jQuery(document).ready(function ($) {
    let $firstHeading = $('.wc-block-product-filters__overlay-content > div').first().find('h3.wp-block-heading');
    $firstHeading.addClass('is-open');
    $firstHeading.next().show();

    $(document).on('click', '.wc-block-product-filters__overlay-content .wp-block-heading', function () {
        let $heading = $(this);
        let $content = $heading.next();

        $content.stop(true, true).slideToggle(300);
        $heading.toggleClass('is-open');
    });


    $('.btn-trigger-filters').click(function () {
        $('button.wc-block-product-filters__open-overlay').trigger('click');
    })

    // "Trusted by us" logo carousel — settings come from the data-slick attribute
    // (set server-side in trusted-by-omecon.php) so PHP stays the single source
    // of truth for slidesToShow / breakpoints.
    $('.omecon-trusted__carousel').each(function () {
        $(this).slick();
    });

    // "Industries" slider — custom round prev/next arrows (section12-omecon.php)
    // live outside the track, in the section header, so they're passed in here
    // instead of via the data-slick attribute (Slick's default arrows can't
    // point at pre-existing DOM elements through pure JSON config).
    $('.omecon-s12__track').each(function () {
        var $track = $(this);
        var $section = $track.closest('.omecon-s12');

        $track.slick({
            slidesToShow: 3.3,
            slidesToScroll: 1,
            infinite: false,
            arrows: true,
            dots: false,
            prevArrow: $section.find('.omecon-s12__arrow--prev'),
            nextArrow: $section.find('.omecon-s12__arrow--next'),
            responsive: [
                { breakpoint: 1200, settings: { slidesToShow: 2 } },
                { breakpoint: 768, settings: { slidesToShow: 1 } },
            ],
        });
    });

    // Section 21 (features_modules) — category pills filter the module
    // cards by data-category. Card layout also depends on how many cards
    // end up visible: 3–4 fill the row evenly, 1–2 switch to a fixed
    // ~496px card (.omecon-s21__cards--few); with exactly 1 visible it's
    // left-aligned instead of centered (.omecon-s21__cards--single), so the
    // count has to be recomputed every time the active category changes.
    document.querySelectorAll('.omecon-s21').forEach(function (section) {
        const pillsWrap = section.querySelector('.omecon-s21__pills');
        const cardsWrap = section.querySelector('.omecon-s21__cards');
        if (!cardsWrap) return;

        const cards = cardsWrap.querySelectorAll('.omecon-s21__card');

        // Equal height, matched to the tallest VISIBLE card only — so it's
        // scoped to whichever category is currently filtered in, not every
        // card in the DOM regardless of filter. Desktop only (1140px up).
        // IMPORTANT: this only ever reads/sets `is-hidden`/minHeight, never
        // touches which cards ARE visible — that stays 100% owned by
        // applyFilter below. Keep it that way; an earlier attempt at this
        // broke the filter (showed every module) by tangling the two.
        let remeasureHeights = null;
        const heightsMedia = gsap.matchMedia();
        heightsMedia.add('(min-width: 1140px)', () => {
            remeasureHeights = () => {
                // Plain style writes, not gsap.set — this isn't animated, and
                // gsap.set(...,{minHeight}) was silently not applying here.
                cards.forEach((card) => {
                    card.style.removeProperty('min-height');
                });
                const visibleCards = Array.from(cards).filter((card) => !card.classList.contains('is-hidden'));
                if (visibleCards.length < 2) {
                    return;
                }
                const tallest = Math.max(...visibleCards.map((card) => card.getBoundingClientRect().height));
                visibleCards.forEach((card) => {
                    card.style.minHeight = tallest + 'px';
                });
            };

            remeasureHeights();

            if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(remeasureHeights);
            }

            let resizeRaf = null;
            const onResize = () => {
                if (resizeRaf) {
                    cancelAnimationFrame(resizeRaf);
                }
                resizeRaf = requestAnimationFrame(remeasureHeights);
            };
            window.addEventListener('resize', onResize, { passive: true });

            return () => {
                window.removeEventListener('resize', onResize);
                cards.forEach((card) => {
                    card.style.removeProperty('min-height');
                });
                remeasureHeights = null;
            };
        });

        const applyFilter = function (slug) {
            let visibleCount = 0;

            cards.forEach(function (card) {
                const categories = (card.dataset.category || '').split(' ').filter(Boolean);
                const matches = !slug || categories.includes(slug);
                card.classList.toggle('is-hidden', !matches);
                if (matches) visibleCount++;
            });

            cardsWrap.classList.toggle('omecon-s21__cards--few', visibleCount > 0 && visibleCount <= 2);
            cardsWrap.classList.toggle('omecon-s21__cards--single', visibleCount === 1);

            if (remeasureHeights) {
                remeasureHeights();
            }
        };

        if (pillsWrap) {
            const activePill = pillsWrap.querySelector('.omecon-s21__pill.is-active');
            applyFilter(activePill ? activePill.dataset.category : '');

            pillsWrap.addEventListener('click', function (e) {
                const pill = e.target.closest('.omecon-s21__pill');
                if (!pill) return;

                pillsWrap.querySelectorAll('.omecon-s21__pill').forEach(function (p) {
                    p.classList.toggle('is-active', p === pill);
                    p.setAttribute('aria-selected', p === pill ? 'true' : 'false');
                });

                applyFilter(pill.dataset.category);
            });
        } else {
            applyFilter('');
        }
    });

    // ── s15 "grid_featured" — widen the media tile on hover ─────
    // Pure CSS (:hover/:has()) has no state "memory" — fast mouse movement
    // between cards could glitch, since during the width transition the
    // cursor is briefly over NO card for a frame, so stateless CSS kept
    // snapping back to the default-active card. Here we remember the
    // last-hovered card and toggle --active on it instead of resetting.
    // While the flex-basis transition (0.35s, see _s15-benefits-tiles.scss)
    // is running, further mouseenter switches are ignored — otherwise
    // hovering across cards mid-animation makes the whole row jump around.
    //
    // The text column (.omecon-s15__content) has no fixed width — it just
    // fills whatever the image isn't using, so it's wider at rest (image
    // hidden) than once a card is active (image takes half). That's a
    // genuine width change (re-wrapping to a different line count is
    // expected, not a bug), so instead of hiding it it's masked: fade the
    // two cards whose active-state is actually flipping out to opacity 0
    // first, swap `--active` once they're invisible, then fade back in
    // once the 0.35s width transition has settled — the reflow itself is
    // never seen happening.
    document.querySelectorAll('.omecon-s15__cards').forEach(function (cardsWrap) {
        const mediaCards = cardsWrap.querySelectorAll('.omecon-s15__card--media');
        if (mediaCards.length < 2) return;

        let switching = false;

        mediaCards.forEach(function (card) {
            card.addEventListener('mouseenter', function () {
                if (switching || card.classList.contains('omecon-s15__card--active')) return;
                switching = true;

                const previouslyActive = Array.from(mediaCards).find(function (c) {
                    return c.classList.contains('omecon-s15__card--active');
                });
                const fading = [card, previouslyActive].filter(Boolean).map(function (c) {
                    return c.querySelector('.omecon-s15__content');
                }).filter(Boolean);

                fading.forEach(function (content) {
                    content.classList.add('is-s15-fading');
                });

                setTimeout(function () {
                    mediaCards.forEach(function (c) {
                        c.classList.toggle('omecon-s15__card--active', c === card);
                    });

                    setTimeout(function () {
                        fading.forEach(function (content) {
                            content.classList.remove('is-s15-fading');
                        });
                        switching = false;
                    }, 350);
                }, 150);
            });
        });
    });
});
